import {createComponent, ElementRef, EnvironmentInjector, Injectable} from '@angular/core';
import {FileInfoData, PreviewFileData}                                from '../types/ap-file-upload.types';
import {FileState}                                                    from '@progress/kendo-angular-upload';
import {
  ApTranslationService
}                                                                     from '../../ap-utils/service/ap-translation.service';
import {ApKendoIconService}                                           from '../../icons/ap-kendo-icon.service';
import {SVGIconComponent}                                             from '@progress/kendo-angular-icons';

/**
 * Service for managing HTML DOM manipulations related to file upload UI components.
 * Handles adding thumbnails, icons, upload dates, and download buttons to file elements.
 */
@Injectable({providedIn: 'root'})
export class FileUploadHtmlService {
  constructor(private environmentInjector: EnvironmentInjector,
              private kendoIconService: ApKendoIconService,
              private translationService: ApTranslationService) {
  }

  /**
   * Adds a thumbnail preview for media files with click-to-zoom functionality
   * @param uploadComponentElementRef Reference to upload component element
   * @param mediaFileData Preview data for the media file
   * @param onClickCallback Callback function to handle thumbnail click
   */
  public addMediaFileThumbnail(uploadComponentElementRef: ElementRef,
                               mediaFileData: PreviewFileData,
                               onClickCallback: (mediaFileData: PreviewFileData) => void): void {
    const fileHtmlElement: any = this._getFileHtmlElement(uploadComponentElementRef, mediaFileData.FileInfoData.uid);
    const childElement = fileHtmlElement?.querySelector('.k-file-icon-wrapper');
    if (!childElement) {
      return;
    }
    const fileElementForPreview = document.createElement('img');
    fileElementForPreview.alt = 'thumbnail';
    fileElementForPreview.src = mediaFileData.ThumbnailDataUrl;
    fileElementForPreview.style.cssText = 'width: 50px; height: 50px; cursor: zoom-in';
    fileElementForPreview.onclick = () => onClickCallback(mediaFileData);
    childElement.replaceWith(fileElementForPreview);
  }

  /**
   * Adds click functionality to file icons for non-media files
   * @param uploadComponentElementRef Reference to upload component element
   * @param mediaFileData Preview data for the file
   * @param onClickCallback Callback function to handle icon click
   */
  public addFileIconClick(uploadComponentElementRef: ElementRef,
                          mediaFileData: PreviewFileData,
                          onClickCallback: (mediaFileData: PreviewFileData) => void): void {
    const fileHtmlElement: any = this._getFileHtmlElement(uploadComponentElementRef, mediaFileData.FileInfoData.uid);
    const fileElementForPreview = fileHtmlElement?.querySelector('.k-file-icon-wrapper');
    if (!fileElementForPreview) {
      return;
    }
    fileElementForPreview.style.cssText = 'cursor: zoom-in';
    fileElementForPreview.onclick = () => onClickCallback(mediaFileData);
  }

  /**
   * Adds upload date information to file element and manages status display
   * @param uploadComponentElementRef Reference to upload component element
   * @param fileInfoData File information containing upload date
   */
  public addUploadDateToFile(uploadComponentElementRef: ElementRef,
                             fileInfoData: FileInfoData): void {
    const fileHtmlElement: any = this._getFileHtmlElement(uploadComponentElementRef, fileInfoData.uid);
    const fileInfoSpanContainer = fileHtmlElement?.querySelector('.k-file-info');
    if (!fileInfoSpanContainer) {
      return;
    }
    const uploadedAtTranslation = this.translationService.translate('UploadedAt');
    const spanForUploadedAt = document.createElement('span');
    spanForUploadedAt.className = 'file-upload-date';
    spanForUploadedAt.innerHTML = `${uploadedAtTranslation} ${fileInfoData.UploadDate.toLocaleDateString()}`;
    fileInfoSpanContainer.append(spanForUploadedAt);
    if (fileInfoData.state === FileState.Uploaded) {
      const fileStatusSpan = fileHtmlElement?.querySelector('.k-file-summary');
      if (!!fileStatusSpan) {
        fileInfoSpanContainer.removeChild(fileStatusSpan);
      }
    }
  }

  /**
   * Adds 'Ready to Upload' status indicator to file element
   * @param uploadComponentElementRef Reference to upload component element
   * @param fileInfoData File information data
   * @param className CSS class name for the status indicator
   */
  public addReadyToUploadInfo(uploadComponentElementRef: ElementRef,
                              fileInfoData: FileInfoData,
                              className: string): void {
    const fileHtmlElement: any = this._getFileHtmlElement(uploadComponentElementRef, fileInfoData.uid);
    const fileNameSpan = fileHtmlElement?.querySelector('.k-file-name');
    if (!fileNameSpan) {
      return;
    }
    const spanForReadyToUpload = document.createElement('span');
    spanForReadyToUpload.className = `k-file-information ${className}`;
    spanForReadyToUpload.innerHTML = this.translationService.translate('ReadyToUpload');
    fileNameSpan.after(spanForReadyToUpload);
  }

  /**
   * Adds download button to file element with click functionality
   * @param uploadComponentElementRef Reference to upload component element
   * @param mediaFileData Preview data for the file
   * @param onClickCallback Callback function to handle download button click
   */
  public addDownloadButton(uploadComponentElementRef: ElementRef,
                           mediaFileData: PreviewFileData,
                           onClickCallback: (mediaFileData: PreviewFileData) => void): void {
    const fileHtmlElement: any = this._getFileHtmlElement(uploadComponentElementRef, mediaFileData.FileInfoData.uid);
    const actionsContainer = fileHtmlElement?.querySelector('.k-upload-actions');
    if (!actionsContainer) {
      return;
    }
    const downloadButton = document.createElement('button');
    downloadButton.type = 'button';
    downloadButton.className = 'k-button k-icon-button k-button-md k-rounded-md k-button-flat k-button-flat-base k-upload-action download-file-button';
    const iconRef = createComponent(SVGIconComponent, {
      environmentInjector: this.environmentInjector
    });
    iconRef.instance.icon = this.kendoIconService.Get('downloadIcon');
    iconRef.changeDetectorRef.detectChanges();
    downloadButton.appendChild(iconRef.location.nativeElement);
    downloadButton.onclick = () => {
      onClickCallback(mediaFileData);
    };
    if (actionsContainer.firstChild) {
      actionsContainer.insertBefore(downloadButton, actionsContainer.firstChild);
    } else {
      actionsContainer.appendChild(downloadButton);
    }
  }

  /**
   * Updates download button state (enabled/disabled)
   * @param uploadComponentElementRef Reference to upload component element
   * @param fileId Unique identifier for the file
   * @param isLoading Boolean indicating if download is in progress
   */
  public downloadButtonState(uploadComponentElementRef: ElementRef, fileId: string, isLoading: boolean): void {
    const fileHtmlElement: any = this._getFileHtmlElement(uploadComponentElementRef, fileId);
    const downloadButton = fileHtmlElement?.querySelector('.download-file-button');
    if (!downloadButton) {
      return;
    }
    downloadButton.disabled = isLoading;
  }

  /**
   * Removes all 'Ready to Upload' status indicators
   * @param uploadComponentElementRef Reference to upload component element
   * @param className CSS class name of status indicators to remove
   */
  public removeAllReadyToUpload(uploadComponentElementRef: ElementRef, className: string): void {
    const readyToUploadSpans = uploadComponentElementRef.nativeElement?.querySelectorAll(
      `.${className}`);
    if (!!readyToUploadSpans && readyToUploadSpans.length > 0) {
      for (const readyToUploadSpan of readyToUploadSpans) {
        readyToUploadSpan.remove();
      }
    }
  }

  /**
   * Retrieves HTML element for a specific file by its ID
   * @param uploadComponentElementRef Reference to upload component element
   * @param fileId Unique identifier for the file
   * @returns HTML element representing the file or null if not found
   * @private
   */
  private _getFileHtmlElement(uploadComponentElementRef: ElementRef, fileId: string): any {
    return uploadComponentElementRef.nativeElement?.querySelector(`[data-uid="${fileId}"]`);
  }
}
